import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Edit, User, Plus, Trash2, Download, Calendar, Mail, Phone,
  Shield, FileText, Camera, Globe, Lock, Pause,
} from 'lucide-react'
import { useState, useEffect, useRef, useMemo, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { personApi, type PersonStatus } from '@/services/api/persons'
import { photoApi, type PhotoItem, type PhotoStatus } from '@/services/api/photos'
import { biodataApi, type BiodataItem } from '@/services/api/biodata'
import { mrfApi, type MRFItem } from '@/services/api/mrf'
import { useAuthorities } from '@/hooks/useAuthorities'
import ModalComponent from '@/components/Modal'
import RichTextEditor from '@/components/RichTextEditor'
import { isHtmlContent, safeTruncate, sanitizeHtml } from '@/utils/textUtils'
import { fileUrl } from '@/utils/fileUrl'
import { formatDate } from '@/lib/utils'

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function avatarColorClass(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return `bk-avatar-c${Math.abs(hash) % 6}`
}
function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]!.toUpperCase()).join('') || '?'
}

function StatusBadge({ status }: { status?: PersonStatus | string }) {
  const value = (status || 'Private') as PersonStatus
  if (value === 'Public') {
    return <span className="bk-badge bk-badge-public bk-badge-dot"><Globe size={11} /> Public</span>
  }
  if (value === 'Inactive') {
    return <span className="bk-badge bk-badge-soft bk-badge-dot"><Pause size={11} /> Inactive</span>
  }
  return <span className="bk-badge bk-badge-private bk-badge-dot"><Lock size={11} /> Private</span>
}

function lastUpdated(items?: { update_date?: string }[]): string | null {
  if (!items || items.length === 0) return null
  const ts = items
    .map(i => (i.update_date ? new Date(i.update_date).getTime() : NaN))
    .filter(n => !Number.isNaN(n))
  if (ts.length === 0) return null
  return formatDate(new Date(Math.max(...ts)))
}

// Scrollspy: track which section is closest to the top of the viewport.
function useScrollspy(ids: string[], topOffset = 100): string {
  const [active, setActive] = useState(ids[0] || '')
  useEffect(() => {
    if (ids.length === 0) return
    const handler = () => {
      let current = ids[0]
      for (const id of ids) {
        const el = document.getElementById(id)
        if (!el) continue
        const rect = el.getBoundingClientRect()
        if (rect.top - topOffset <= 0) current = id
      }
      setActive(current)
    }
    handler()
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [ids, topOffset])
  return active
}

// ─────────────────────────────────────────────────────────────────────────
// Page root
// ─────────────────────────────────────────────────────────────────────────

export default function PersonDetailPage() {
  const { id } = useParams()
  const personId = Number(id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canViewPhotos, canViewBiodata, canViewMRF, canDeletePerson } = useAuthorities()

  const { data: person, isLoading: personLoading } = useQuery({
    queryKey: ['person', personId],
    queryFn: () => personApi.getPerson(personId),
    enabled: !!personId,
  })

  const deletePersonMutation = useMutation({
    mutationFn: personApi.deletePerson,
    onSuccess: () => {
      toast.success('Profile deleted')
      queryClient.invalidateQueries({ queryKey: ['persons'] })
      navigate('/persons')
    },
    onError: () => toast.error('Failed to delete profile'),
  })

  const sectionIds = useMemo(() => {
    const out = ['info']
    if (canViewPhotos())  out.push('photos')
    if (canViewBiodata()) out.push('biodata')
    if (canViewMRF())     out.push('mrf')
    return out
  }, [canViewPhotos, canViewBiodata, canViewMRF])

  const activeSection = useScrollspy(sectionIds, 120)

  const goTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  if (personLoading) {
    return (
      <div style={{ padding: 32, maxWidth: 1280, margin: '0 auto' }}>
        <div className="bk-skel" style={{ height: 28, width: '24%', marginBottom: 16 }} />
        <div className="bk-skel" style={{ height: 200, marginBottom: 18, borderRadius: 24 }} />
        <div className="bk-skel" style={{ height: 320, borderRadius: 16 }} />
      </div>
    )
  }

  if (!person) {
    return (
      <div style={{ padding: 32, maxWidth: 1280, margin: '0 auto' }}>
        <div className="bk-empty">
          <div className="icon"><User size={22} /></div>
          <div className="ttl">Person not found</div>
          <div className="sub">The person you're looking for doesn't exist or has been removed.</div>
          <button type="button" className="bk-btn bk-btn-secondary" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </div>
    )
  }

  const name = person.display_name || 'Unnamed Person'

  return (
    <div style={{ background: 'var(--bk-bg)', minHeight: '100%' }}>
      {/* Breadcrumb bar — Back + path + ID pill */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 32px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          className="bk-btn bk-btn-ghost bk-btn-sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ fontSize: 13, color: 'var(--bk-muted)', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span>Persons</span>
          <span style={{ opacity: .5 }}>/</span>
          <strong className="bk-truncate" style={{ color: 'var(--bk-ink)' }} title={name}>{name}</strong>
        </div>
        <div style={{ flex: 1 }} />
      </div>

      {/* Two-column layout */}
      <div className="bk-detail">
        {/* Sticky section nav */}
        <aside className="bk-detail-nav" aria-label="Sections">
          <button
            type="button"
            className="bk-detail-nav-item"
            data-active={activeSection === 'info'}
            onClick={() => goTo('info')}
          >
            <User size={15} /> Person info
          </button>
          {canViewPhotos() && (
            <button
              type="button"
              className="bk-detail-nav-item"
              data-active={activeSection === 'photos'}
              onClick={() => goTo('photos')}
            >
              <Camera size={15} /> Photos
            </button>
          )}
          {canViewBiodata() && (
            <button
              type="button"
              className="bk-detail-nav-item"
              data-active={activeSection === 'biodata'}
              onClick={() => goTo('biodata')}
            >
              <FileText size={15} /> Biodata
            </button>
          )}
          {canViewMRF() && (
            <button
              type="button"
              className="bk-detail-nav-item"
              data-active={activeSection === 'mrf'}
              onClick={() => goTo('mrf')}
            >
              <Shield size={15} /> Media forms
            </button>
          )}
          {canDeletePerson() && (
            <>
              <div style={{ borderTop: '1px solid var(--bk-border)', margin: '8px 0' }} />
              <button
                type="button"
                className="bk-detail-nav-item"
                style={{ color: 'var(--bk-danger)' }}
                onClick={() => {
                  if (window.confirm(`Delete ${name}? This cannot be undone.`)) {
                    deletePersonMutation.mutate(person.id)
                  }
                }}
              >
                <Trash2 size={15} /> Delete profile
              </button>
            </>
          )}
        </aside>

        {/* Main content */}
        <div className="bk-detail-main">
          <PersonHeroSection id="info" person={person} />
          {canViewPhotos()  && <PhotosSection  id="photos"  personId={personId} />}
          {canViewBiodata() && <BiodataSection id="biodata" personId={personId} />}
          {canViewMRF()     && <MRFSection     id="mrf"     personId={personId} />}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Person hero
// ─────────────────────────────────────────────────────────────────────────

function PersonHeroSection({ id, person }: { id: string; person: any }) {
  const navigate = useNavigate()
  const { canEditPerson } = useAuthorities()
  const name = person.display_name || 'Unnamed Person'

  return (
    <section id={id} className="bk-person-hero">
      <div className={`bk-avatar bk-avatar-xl ${avatarColorClass(name)}`}>{initials(name)}</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <StatusBadge status={person.status} />
          {person.profile_type && (
            <span className={`bk-badge ${person.profile_type === 'BK' ? 'bk-badge-bk' : 'bk-badge-guest'}`}>
              {person.profile_type}
            </span>
          )}
          <span style={{ fontSize: 12.5, color: 'var(--bk-muted)' }}>Brahma Kumaris</span>
        </div>

        <h1 className="name">{name}</h1>
        <div className="role">{person.email || '—'}</div>

        <div className="row">
          <div className="item">
            <span className="lbl">Email</span>
            <span className="val"><Mail size={14} style={{ color: 'var(--bk-muted)' }} /> {person.email || '—'}</span>
          </div>
          <div className="item">
            <span className="lbl">Phone</span>
            <span className="val"><Phone size={14} style={{ color: 'var(--bk-muted)' }} /> {person.phone || '—'}</span>
          </div>
          <div className="item">
            <span className="lbl">Last edited</span>
            <span className="val">
              <Calendar size={14} style={{ color: 'var(--bk-muted)' }} />
              {person.update_date ? formatDate(person.update_date) : '—'}
            </span>
          </div>
        </div>

        {person.tags?.length > 0 && (
          <div className="bk-tag-row" style={{ marginTop: 10 }}>
            {person.tags.map((t: string) => <span key={t} className="bk-tag-chip">{t}</span>)}
          </div>
        )}
      </div>

      <div className="actions">
        {canEditPerson() && (
          <button
            type="button"
            className="bk-btn bk-btn-secondary"
            onClick={() => navigate(`/persons/${person.id}/edit`)}
          >
            <Edit size={14} /> Edit
          </button>
        )}
      </div>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Photos section
// ─────────────────────────────────────────────────────────────────────────

function PhotosSection({ id, personId }: { id: string; personId: number }) {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const queryClient = useQueryClient()
  const authorities = useAuthorities()

  const { data: photos, isLoading } = useQuery({
    queryKey: ['photos', personId],
    queryFn: () => photoApi.listByPerson(personId, 1, 50),
    enabled: !!personId,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['photos', personId] })
    queryClient.invalidateQueries({ queryKey: ['person', Number(personId)] })
    queryClient.invalidateQueries({ queryKey: ['persons'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
  }

  const uploadMutation = useMutation({
    // All uploads default to Private; user moves to Public via the toggle on the card.
    mutationFn: (file: File) => photoApi.upload(personId, file, { status: 'Private' }),
    onSuccess: () => {
      toast.success('Photo uploaded as Private')
      setShowUploadModal(false)
      setUploadFile(null)
      invalidate()
    },
    onError: () => toast.error('Failed to upload photo'),
  })

  const deleteMutation = useMutation({
    mutationFn: (photoId: number) => photoApi.remove(photoId),
    onSuccess: () => { toast.success('Photo deleted'); invalidate() },
    onError: () => toast.error('Failed to delete photo'),
  })

  const setStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: PhotoStatus }) => photoApi.setStatus(id, status),
    onSuccess: (_d, vars) => { toast.success(`Photo moved to ${vars.status}`); invalidate() },
    onError: () => toast.error('Failed to change photo status'),
  })

  const allItems: PhotoItem[] = photos?.items ?? []
  const publicItems  = allItems.filter(p => p.status === 'Public')
  const privateItems = allItems.filter(p => p.status !== 'Public')
  const updated = lastUpdated(allItems)

  return (
    <section id={id} className="bk-card">
      <header className="bk-card-hd">
        <div className="icon-tile tile-photos"><Camera size={18} /></div>
        <div className="hd-text">
          <div className="hd-title">Photos</div>
          <div className="hd-sub">
            New uploads start as Private — use the toggle on a photo to publish.
            {updated && <> · Updated {updated}</>}
          </div>
        </div>
        {authorities.canManagePhotos() && (
          <button
            type="button"
            className="bk-btn bk-btn-primary"
            onClick={() => { setUploadFile(null); setShowUploadModal(true) }}
          >
            <Plus size={14} /> Add photo
          </button>
        )}
      </header>

      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {isLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 12 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bk-skel" style={{ aspectRatio: '1 / 1', borderRadius: 12 }} />
            ))}
          </div>
        ) : (
          <>
            <PhotoGroup
              status="public"
              label="Public"
              icon={<Globe size={11} />}
              items={publicItems}
              onMove={photoId => setStatusMutation.mutate({ id: photoId, status: 'Private' })}
              moveLabel="Move to Private"
              authorities={authorities}
              deleteMutation={deleteMutation}
            />
            {authorities.canSeeAllPersons() && (
              <PhotoGroup
                status="private"
                label="Private"
                icon={<Lock size={11} />}
                items={privateItems}
                onMove={photoId => setStatusMutation.mutate({ id: photoId, status: 'Public' })}
                moveLabel="Move to Public"
                authorities={authorities}
                deleteMutation={deleteMutation}
              />
            )}
          </>
        )}
      </div>

      {showUploadModal && (
        <ModalComponent onClose={() => setShowUploadModal(false)} title="Upload Photo">
          <div className="space-y-4">
            <div>
              <label className="bk-label">Select image file</label>
              <input
                type="file"
                accept="image/*"
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                className="bk-input"
                style={{ padding: '8px 12px', lineHeight: '24px' }}
              />
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--bk-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Lock size={12} />
              Will upload as <strong style={{ margin: '0 4px', color: 'var(--bk-ink)' }}>Private</strong>. You can publish it later.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="bk-btn bk-btn-ghost" onClick={() => setShowUploadModal(false)}>Cancel</button>
              <button
                className="bk-btn bk-btn-primary"
                onClick={() => uploadFile && uploadMutation.mutate(uploadFile)}
                disabled={!uploadFile || uploadMutation.isPending}
              >
                {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </section>
  )
}

function PhotoGroup({
  status, label, icon, items, onMove, moveLabel, authorities, deleteMutation,
}: {
  status: 'public' | 'private'
  label: string
  icon: ReactNode
  items: PhotoItem[]
  onMove: (id: number) => void
  moveLabel: string
  authorities: ReturnType<typeof useAuthorities>
  deleteMutation: { mutate: (id: number) => void; isPending: boolean }
}) {
  return (
    <div className="bk-photo-group" data-status={status}>
      <div className="hd">
        <span className={`bk-badge ${status === 'public' ? 'bk-badge-public' : 'bk-badge-private'}`}>{icon} {label}</span>
        <span style={{ fontSize: 12.5, color: 'var(--bk-muted)' }}>
          {items.length} {items.length === 1 ? 'photo' : 'photos'}
        </span>
        {status === 'private' && items.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--bk-muted)', marginLeft: 'auto' }}>
            Tap a photo's toggle to publish
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bk-empty" style={{ padding: '28px 12px' }}>
          <Camera size={22} />
          <div className="sub">No {label.toLowerCase()} photos yet.</div>
        </div>
      ) : (
        <div className="bk-photo-grid">
          {items.map(photo => (
            <div key={photo.id} className="bk-photo">
              <img
                src={fileUrl(photo.photo_url) || '/placeholder-image.png'}
                alt={photo.title || 'Photo'}
              />
              <span className={`bk-badge ${status === 'public' ? 'bk-badge-public' : 'bk-badge-private'} pill`}>
                {icon} {label}
              </span>
              <div className="overlay">
                <span className="bk-truncate" style={{ flex: 1, minWidth: 0, fontSize: 11 }}>{photo.title || photo.filename}</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {authorities.canManagePhotos() && (
                    <button
                      type="button"
                      className="bk-icon-btn bk-icon-btn-sm"
                      style={{ background: 'rgba(255,255,255,0.18)', color: '#fff' }}
                      onClick={() => onMove(photo.id)}
                      title={moveLabel}
                      aria-label={moveLabel}
                    >
                      {status === 'public' ? <Lock size={14} /> : <Globe size={14} />}
                    </button>
                  )}
                  <button
                    type="button"
                    className="bk-icon-btn bk-icon-btn-sm"
                    style={{ background: 'rgba(255,255,255,0.18)', color: '#fff' }}
                    onClick={() => {
                      const url = fileUrl(photo.photo_url)
                      if (url) {
                        const a = document.createElement('a')
                        a.href = url; a.download = photo.filename; a.click()
                      }
                    }}
                    title="Download photo"
                    aria-label="Download photo"
                  >
                    <Download size={14} />
                  </button>
                  {authorities.canDeletePhotos() && (
                    <button
                      type="button"
                      className="bk-icon-btn bk-icon-btn-sm"
                      style={{ background: 'rgba(178,59,46,0.85)', color: '#fff' }}
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this photo?')) {
                          deleteMutation.mutate(photo.id)
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      title="Delete photo"
                      aria-label="Delete photo"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Biodata section
// ─────────────────────────────────────────────────────────────────────────

function BiodataSection({ id, personId }: { id: string; personId: number }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingItem, setEditingItem] = useState<BiodataItem | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    date: new Date().toISOString().split('T')[0],
  })
  const queryClient = useQueryClient()
  const authorities = useAuthorities()

  const { data: biodata, isLoading } = useQuery({
    queryKey: ['biodata', personId],
    queryFn: () => biodataApi.listByPerson(personId, 1, 50),
    enabled: !!personId,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['biodata', personId] })
    queryClient.invalidateQueries({ queryKey: ['person', Number(personId)] })
    queryClient.invalidateQueries({ queryKey: ['persons'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
  }

  const createMutation = useMutation({
    mutationFn: () => biodataApi.create(personId, {
      bio_data_title: formData.title,
      bio_data: formData.content,
      bio_data_date: formData.date,
    }),
    onSuccess: () => {
      toast.success('Biodata created successfully')
      setShowCreateModal(false)
      setFormData({ title: '', content: '', date: new Date().toISOString().split('T')[0] })
      invalidate()
    },
    onError: (err: any) => {
      const msg = err.response?.data?.detail || err.message || 'Failed to create biodata'
      toast.error(msg)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (bid: number) => biodataApi.update(bid, {
      bio_data_title: formData.title,
      bio_data: formData.content,
      bio_data_date: formData.date,
    }),
    onSuccess: () => {
      toast.success('Biodata updated successfully')
      setShowEditModal(false)
      setEditingItem(null)
      setFormData({ title: '', content: '', date: new Date().toISOString().split('T')[0] })
      invalidate()
    },
    onError: (err: any) => {
      const msg = err.response?.data?.detail || err.message || 'Failed to update biodata'
      toast.error(msg)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (bid: number) => biodataApi.remove(bid),
    onSuccess: () => { toast.success('Biodata deleted'); invalidate() },
    onError: () => toast.error('Failed to delete biodata'),
  })

  const toggleExpanded = (bid: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev)
      if (next.has(bid)) next.delete(bid); else next.add(bid)
      return next
    })
  }

  const handleEdit = (item: BiodataItem) => {
    setEditingItem(item)
    setFormData({
      title: item.bio_data_title || '',
      content: item.bio_data || '',
      date: item.bio_data_date || '',
    })
    setShowEditModal(true)
  }

  const handleDelete = (bid: number) => {
    if (window.confirm('Are you sure you want to delete this biodata entry?')) {
      deleteMutation.mutate(bid)
    }
  }

  const updated = lastUpdated(biodata?.items)
  const items: BiodataItem[] = biodata?.items ?? []

  return (
    <section id={id} className="bk-card">
      <header className="bk-card-hd">
        <div className="icon-tile tile-bio"><FileText size={18} /></div>
        <div className="hd-text">
          <div className="hd-title">Biodata</div>
          <div className="hd-sub">
            {items.length} {items.length === 1 ? 'entry' : 'entries'}
            {updated && <> · Updated {updated}</>}
          </div>
        </div>
        {authorities.canManageBiodata() && (
          <button
            type="button"
            className="bk-btn bk-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={14} /> New biodata
          </button>
        )}
      </header>

      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {isLoading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bk-skel" style={{ height: 140, borderRadius: 16 }} />
          ))
        ) : items.length === 0 ? (
          <div className="bk-empty">
            <div className="icon"><FileText size={22} /></div>
            <div className="ttl">No biodata yet</div>
            <div className="sub">Start by creating the first biodata entry for this person.</div>
            {authorities.canManageBiodata() && (
              <button type="button" className="bk-btn bk-btn-primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={14} /> Create first biodata
              </button>
            )}
          </div>
        ) : items.map(item => {
          const isExpanded = expandedItems.has(item.id)
          const content = item.bio_data || ''
          const shouldTruncate = content.length > 220
          const displayContent = isExpanded || !shouldTruncate ? content : safeTruncate(content)
          return (
            <article key={item.id} className={`bk-bio ${shouldTruncate && !isExpanded ? 'collapsed' : ''}`}>
              <div className="hd">
                <div>
                  <div className="title">{item.bio_data_title || 'Untitled'}</div>
                  <div className="date">
                    <Calendar size={13} />
                    {item.bio_data_date ? formatDate(item.bio_data_date) : 'No date'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {authorities.canManageBiodata() && (
                    <button
                      type="button"
                      className="bk-icon-btn bk-icon-btn-sm"
                      onClick={() => handleEdit(item)}
                      aria-label="Edit biodata"
                      title="Edit biodata"
                    >
                      <Edit size={14} />
                    </button>
                  )}
                  {authorities.canDeleteBiodata() && (
                    <button
                      type="button"
                      className="bk-icon-btn bk-icon-btn-sm bk-icon-btn-danger"
                      onClick={() => handleDelete(item.id)}
                      aria-label="Delete biodata"
                      title="Delete biodata"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="body">
                {isHtmlContent(displayContent)
                  ? <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayContent) }} />
                  : <div style={{ whiteSpace: 'pre-wrap' }}>{displayContent}</div>}
              </div>
              {shouldTruncate && (
                <button type="button" className="more" onClick={() => toggleExpanded(item.id)}>
                  {isExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </article>
          )
        })}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <ModalComponent onClose={() => setShowCreateModal(false)} title="Create New Biodata">
          <div className="space-y-4">
            <div>
              <label className="bk-label">Biodata title</label>
              <input
                type="text"
                className="bk-input"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter biodata title"
              />
            </div>
            <div>
              <label className="bk-label">Biodata content</label>
              <RichTextEditor
                value={formData.content}
                onChange={value => setFormData({ ...formData, content: value })}
                placeholder="Enter biodata content..."
                height="200px"
                className="w-full"
              />
            </div>
            <div>
              <label className="bk-label">Biodata date *</label>
              <input
                type="date"
                className="bk-input"
                value={formData.date}
                onChange={e => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="bk-btn bk-btn-ghost" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button
                className="bk-btn bk-btn-primary"
                onClick={() => createMutation.mutate()}
                disabled={!formData.title || !formData.content || !formData.date || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </ModalComponent>
      )}

      {/* Edit modal */}
      {showEditModal && editingItem && (
        <ModalComponent onClose={() => setShowEditModal(false)} title="Edit Biodata">
          <div className="space-y-4">
            <div>
              <label className="bk-label">Biodata title</label>
              <input
                type="text"
                className="bk-input"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter biodata title"
              />
            </div>
            <div>
              <label className="bk-label">Biodata content</label>
              <RichTextEditor
                value={formData.content}
                onChange={value => setFormData({ ...formData, content: value })}
                placeholder="Enter biodata content..."
                height="200px"
                className="w-full"
              />
            </div>
            <div>
              <label className="bk-label">Biodata date *</label>
              <input
                type="date"
                className="bk-input"
                value={formData.date}
                onChange={e => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="bk-btn bk-btn-ghost" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button
                className="bk-btn bk-btn-primary"
                onClick={() => updateMutation.mutate(editingItem.id)}
                disabled={!formData.title || !formData.content || !formData.date || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Updating…' : 'Update'}
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// MRF section
// ─────────────────────────────────────────────────────────────────────────

function MRFSection({ id, personId }: { id: string; personId: number }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingItem, setEditingItem] = useState<MRFItem | null>(null)
  const [formData, setFormData] = useState({ eventTitle: '', eventDate: '', file: null as File | null })
  const queryClient = useQueryClient()
  const authorities = useAuthorities()

  const { data: mrfs, isLoading } = useQuery({
    queryKey: ['mrf', personId],
    queryFn: () => mrfApi.listByPerson(personId, 1, 50),
    enabled: !!personId,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mrf', personId] })
    queryClient.invalidateQueries({ queryKey: ['persons'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
  }

  const createMutation = useMutation({
    mutationFn: () => mrfApi.create(personId, {
      event_title: formData.eventTitle,
      event_date: formData.eventDate,
      file: formData.file || undefined,
    }),
    onSuccess: () => {
      toast.success('Media Release Form created')
      setShowCreateModal(false)
      setFormData({ eventTitle: '', eventDate: '', file: null })
      invalidate()
    },
    onError: () => toast.error('Failed to create MRF'),
  })

  const updateMutation = useMutation({
    mutationFn: () => mrfApi.update(editingItem!.id, {
      event_title: formData.eventTitle,
      event_date: formData.eventDate,
      file: formData.file || undefined,
    }),
    onSuccess: () => {
      toast.success('MRF updated')
      setShowEditModal(false)
      setEditingItem(null)
      setFormData({ eventTitle: '', eventDate: '', file: null })
      invalidate()
    },
    onError: () => toast.error('Failed to update MRF'),
  })

  const deleteMutation = useMutation({
    mutationFn: (mrfId: number) => mrfApi.remove(mrfId),
    onSuccess: () => { toast.success('MRF deleted'); invalidate() },
    onError: () => toast.error('Failed to delete MRF'),
  })

  const downloadPdf = (mrf: MRFItem) => {
    const url = fileUrl(mrf.file_url)
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = mrf.form_filename || `mrf_${mrf.id}.pdf`
    a.target = '_blank'
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  const updated = lastUpdated(mrfs?.items)
  const items: MRFItem[] = mrfs?.items ?? []

  return (
    <section id={id} className="bk-card">
      <header className="bk-card-hd">
        <div className="icon-tile tile-mrf"><Shield size={18} /></div>
        <div className="hd-text">
          <div className="hd-title">Media Release Forms</div>
          <div className="hd-sub">
            {items.length} {items.length === 1 ? 'form' : 'forms'}
            {updated && <> · Updated {updated}</>}
          </div>
        </div>
        {authorities.canManageMRF() && (
          <button
            type="button"
            className="bk-btn bk-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={14} /> Add MRF
          </button>
        )}
      </header>

      <div>
        {isLoading ? (
          <div style={{ padding: 24, display: 'grid', gap: 8 }}>
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="bk-skel" style={{ height: 48 }} />)}
          </div>
        ) : items.length === 0 ? (
          <div className="bk-empty">
            <div className="icon"><Shield size={22} /></div>
            <div className="ttl">No media release forms yet</div>
            <div className="sub">Add the first MRF to track event consent for this person.</div>
            {authorities.canManageMRF() && (
              <button type="button" className="bk-btn bk-btn-primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={14} /> Add MRF
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Column header — hidden on mobile via media query */}
            <div className="bk-mrf-row bk-hide-mobile" style={{ borderTop: 0 }}>
              <div className="bk-uppercase" style={{ color: 'var(--bk-muted)' }}>Event title</div>
              <div className="bk-uppercase" style={{ color: 'var(--bk-muted)' }}>Event date</div>
              <div className="bk-uppercase" style={{ color: 'var(--bk-muted)' }}>Form</div>
              <div className="bk-uppercase" style={{ color: 'var(--bk-muted)', justifySelf: 'end' }}>Actions</div>
            </div>
            {items.map(mrf => (
              <div key={mrf.id} className="bk-mrf-row">
                <div className="ttl">{mrf.event_title || '—'}</div>
                <div className="date">{mrf.event_date ? formatDate(mrf.event_date) : '—'}</div>
                <div>
                  {mrf.file_url ? (
                    <button type="button" className="dl" onClick={() => downloadPdf(mrf)}>
                      <Download size={14} /> Download PDF
                    </button>
                  ) : (
                    <span style={{ fontSize: 13, color: 'var(--bk-muted)' }}>No form uploaded</span>
                  )}
                </div>
                <div className="actions">
                  {authorities.canManageMRF() && (
                    <button
                      type="button"
                      className="bk-icon-btn bk-icon-btn-sm"
                      onClick={() => {
                        setEditingItem(mrf)
                        setFormData({
                          eventTitle: mrf.event_title || '',
                          eventDate: mrf.event_date ? new Date(mrf.event_date).toISOString().split('T')[0] : '',
                          file: null,
                        })
                        setShowEditModal(true)
                      }}
                      aria-label="Edit MRF"
                      title="Edit MRF"
                    >
                      <Edit size={14} />
                    </button>
                  )}
                  {authorities.canDeleteMRF() && (
                    <button
                      type="button"
                      className="bk-icon-btn bk-icon-btn-sm bk-icon-btn-danger"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this Media Release Form?')) {
                          deleteMutation.mutate(mrf.id)
                        }
                      }}
                      aria-label="Delete MRF"
                      title="Delete MRF"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <ModalComponent onClose={() => setShowCreateModal(false)} title="Add Media Release Form">
          <div className="space-y-4">
            <div>
              <label className="bk-label">Event title</label>
              <input
                type="text"
                className="bk-input"
                value={formData.eventTitle}
                onChange={e => setFormData({ ...formData, eventTitle: e.target.value })}
                placeholder="Enter event title"
              />
            </div>
            <div>
              <label className="bk-label">Event date</label>
              <input
                type="date"
                className="bk-input"
                value={formData.eventDate}
                onChange={e => setFormData({ ...formData, eventDate: e.target.value })}
              />
            </div>
            <div>
              <label className="bk-label">Upload form (PDF / DOC)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={e => setFormData({ ...formData, file: e.target.files?.[0] || null })}
                className="bk-input"
                style={{ padding: '8px 12px', lineHeight: '24px' }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="bk-btn bk-btn-ghost" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button
                className="bk-btn bk-btn-primary"
                onClick={() => createMutation.mutate()}
                disabled={!formData.eventTitle || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </ModalComponent>
      )}

      {/* Edit modal */}
      {showEditModal && editingItem && (
        <ModalComponent onClose={() => setShowEditModal(false)} title="Edit Media Release Form">
          <div className="space-y-4">
            <div>
              <label className="bk-label">Event title</label>
              <input
                type="text"
                className="bk-input"
                value={formData.eventTitle}
                onChange={e => setFormData({ ...formData, eventTitle: e.target.value })}
                placeholder="Enter event title"
              />
            </div>
            <div>
              <label className="bk-label">Event date</label>
              <input
                type="date"
                className="bk-input"
                value={formData.eventDate}
                onChange={e => setFormData({ ...formData, eventDate: e.target.value })}
              />
            </div>
            <div>
              <label className="bk-label">Upload new form (optional)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={e => setFormData({ ...formData, file: e.target.files?.[0] || null })}
                className="bk-input"
                style={{ padding: '8px 12px', lineHeight: '24px' }}
              />
              {editingItem.file_url && (
                <p style={{ fontSize: 12.5, color: 'var(--bk-muted)', marginTop: 6 }}>
                  Current file: {editingItem.form_filename}
                </p>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="bk-btn bk-btn-ghost" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button
                className="bk-btn bk-btn-primary"
                onClick={() => updateMutation.mutate()}
                disabled={!formData.eventTitle || updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Updating…' : 'Update'}
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </section>
  )
}
